import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import SearchBar from '../components/common/SearchBar';
import SearchResults from '../components/search/SearchResults';
import PlansModal from '../components/common/PlansModal';
import { useSearch } from '../hooks/useSearch';
import { useAuth } from '../context/AuthContext';
import { subscriptionApi, CurrentSubDto, SubCheckResponse } from '../api/subscriptionApi';
import toast from 'react-hot-toast';

const CODE_TYPE_TABS = [
    { value: '', label: 'All Types' },
    { value: 'ICD10', label: 'ICD-10-CM' },
    { value: 'ICD10PCS', label: 'ICD-10-PCS' },
    { value: 'HCPCS', label: 'HCPCS' },
    { value: 'NDC', label: 'NDC' },
    { value: 'LOINC', label: 'LOINC' },
    { value: 'RXNORM', label: 'RxNorm' },
];

const GuestLimitModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
    const navigate = useNavigate();
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
                <div className="w-14 h-14 bg-amber-50 border border-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="text-amber-500" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                        <line x1="12" y1="9" x2="12" y2="13"/>
                        <line x1="12" y1="17" x2="12.01" y2="17"/>
                    </svg>
                </div>
                <h2 className="text-lg font-bold text-gray-900 mb-2">Daily Limit Reached</h2>
                <p className="text-sm text-gray-500 leading-relaxed mb-6">
                    You've used your <strong>2 free searches</strong> for today.
                    Login to your account or create a free account to continue.
                </p>
                <div className="space-y-2.5">
                    <button onClick={() => { navigate('/login'); onClose(); }}
                        className="w-full py-3 bg-teal-600 hover:bg-teal-700 text-white text-sm font-bold rounded-xl transition-all flex items-center justify-center gap-2">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                            <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
                            <polyline points="10 17 15 12 10 7"/>
                            <line x1="15" y1="12" x2="3" y2="12"/>
                        </svg>
                        Login to Your Account
                    </button>
                    <button onClick={() => { navigate('/register'); onClose(); }}
                        className="w-full py-3 border border-teal-200 text-teal-700 text-sm font-bold rounded-xl hover:bg-teal-50 transition-all">
                        Create Free Account
                    </button>
                    <button onClick={onClose} className="w-full py-2 text-sm text-gray-400 hover:text-gray-600 transition-colors">
                        Dismiss
                    </button>
                </div>
                <p className="text-[10px] text-gray-300 mt-4">Free accounts get 2 searches/day · Upgrade for more</p>
            </div>
        </div>
    );
};

const SearchCounter = ({ sub, guestCount, isLoggedIn }: {
    sub: CurrentSubDto | null;
    guestCount: SubCheckResponse | null;
    isLoggedIn: boolean;
}) => {
    if (isLoggedIn && sub) {
        if (sub.searchLimit === -1) {
            return (
                <div className="flex items-center gap-2 mt-3 px-1">
                    <svg className="text-teal-500 flex-shrink-0" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                        <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    <span className="text-[11px] text-teal-600 font-semibold">
                        Unlimited searches — {sub.planName} Plan
                    </span>
                </div>
            );
        }
        const pct = Math.min(100, Math.round((sub.searchesUsed / sub.searchLimit) * 100));
        const isNear = pct >= 80;
        const barColor = pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-400' : 'bg-teal-500';
        return (
            <div className="mt-3 px-1">
                <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-1.5">
                        <svg className="text-gray-400 flex-shrink-0" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                        </svg>
                        <span className="text-[11px] text-gray-500 font-medium">
                            <span className={`font-bold ${isNear ? 'text-amber-600' : 'text-gray-700'}`}>{sub.searchesUsed}</span>
                            {' '}of{' '}
                            <span className="font-bold text-gray-700">{sub.searchLimit.toLocaleString()}</span>
                            {' '}searches used{sub.searchLimit <= 2 ? ' today' : ' this month'}
                        </span>
                    </div>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isNear ? 'bg-amber-50 text-amber-600' : 'bg-gray-50 text-gray-500'}`}>
                        {sub.planName}
                    </span>
                </div>
                <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all duration-500 ${barColor}`} style={{ width: `${pct}%` }}/>
                </div>
            </div>
        );
    }
    if (!isLoggedIn && guestCount) {
        const used = guestCount.searchesUsed;
        const limit = guestCount.searchLimit;
        const pct = Math.min(100, Math.round((used / limit) * 100));
        const barColor = pct >= 100 ? 'bg-red-500' : pct >= 50 ? 'bg-amber-400' : 'bg-teal-500';
        return (
            <div className="mt-3 px-1">
                <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-1.5">
                        <svg className="text-gray-400 flex-shrink-0" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                        </svg>
                        <span className="text-[11px] text-gray-500 font-medium">
                            <span className={`font-bold ${pct >= 100 ? 'text-red-600' : 'text-gray-700'}`}>{used}</span>
                            {' '}of{' '}
                            <span className="font-bold text-gray-700">{limit}</span>
                            {' '}free searches used today
                        </span>
                    </div>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-50 text-gray-500">Guest</span>
                </div>
                <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all duration-500 ${barColor}`} style={{ width: `${pct}%` }}/>
                </div>
            </div>
        );
    }
    return null;
};

const SearchPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const { isLoggedIn } = useAuth();

    const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || '');
    const [codeType, setCodeType] = useState(searchParams.get('type') || '');
    const [category, setCategory] = useState(searchParams.get('cat') || '');

    const [guestModalOpen, setGuestModalOpen] = useState(false);
    const [plansOpen, setPlansOpen] = useState(false);
    const [planMessage, setPlanMessage] = useState('');
    const [planReason, setPlanReason] = useState('');

    const [currentSub, setCurrentSub] = useState<CurrentSubDto | null>(null);
    const [guestCount, setGuestCount] = useState<SubCheckResponse | null>(null);

    const { data, isLoading, error, refetch } = useSearch(searchTerm, codeType, category);

    const loadCounter = useCallback(async () => {
        if (isLoggedIn) {
            const sub = await subscriptionApi.getCurrentSubscription();
            setCurrentSub(sub);
        } else {
            try {
                const guest = await subscriptionApi.getGuestCount();
                setGuestCount(guest);
            } catch {}
        }
    }, [isLoggedIn]);

    useEffect(() => { loadCounter(); }, [loadCounter]);

    useEffect(() => {
        setSearchTerm(searchParams.get('q') || '');
        setCodeType(searchParams.get('type') || '');
        setCategory(searchParams.get('cat') || '');
    }, [searchParams]);

    useEffect(() => {
        if (!error) return;
        const err = error as any;
        const status = err?.response?.status;
        const resData = err?.response?.data;
        if (status === 401) {
            toast.error('Please login to continue');
            navigate('/login');
        } else if (status === 402) {
            if (!isLoggedIn) {
                setGuestModalOpen(true);
            } else {
                setPlanMessage(resData?.message || 'Upgrade your plan to continue searching.');
                setPlanReason(resData?.reason || 'limit_reached');
                setPlansOpen(true);
            }
            loadCounter();
        }
    }, [error, navigate, isLoggedIn, loadCounter]);

    const buildParams = (q: string, type: string, cat: string) => {
        const p: Record<string, string> = {};
        if (q) p.q = q;
        if (type) p.type = type;
        if (cat) p.cat = cat;
        return p;
    };

    const handleSearch = (term: string) => {
        setSearchTerm(term);
        setCategory('');
        setSearchParams(buildParams(term, codeType, ''));
        refetch();
        setTimeout(loadCounter, 500);
    };

    const handleTypeChange = (type: string) => {
        setCodeType(type);
        setCategory('');
        setSearchParams(buildParams(searchTerm, type, ''));
        refetch();
    };

    return (
        <div className="max-w-screen-xl mx-auto px-4 py-4 md:py-6">

            {/* Search bar + Counter */}
            <div className="bg-white border border-gray-100 rounded-xl p-4 mb-4 shadow-sm">
                <SearchBar onSearch={handleSearch} defaultValue={searchTerm} />
                <SearchCounter sub={currentSub} guestCount={guestCount} isLoggedIn={isLoggedIn} />
            </div>

            {/* Category badge */}
            {category && (
                <div className="flex items-center gap-2 mb-3">
                    <span className="text-[11px] text-gray-400">Filtered by:</span>
                    <span className="flex items-center gap-1.5 text-[11px] font-semibold bg-teal-50 text-teal-700 border border-teal-100 px-2.5 py-1 rounded-full">
                        {category}
                        <button onClick={() => { setCategory(''); setSearchParams(buildParams(searchTerm, codeType, '')); refetch(); }}>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                                <path d="M18 6 6 18M6 6l12 12"/>
                            </svg>
                        </button>
                    </span>
                </div>
            )}

            {/* Code type tabs — scrollable on mobile */}
            <div className="flex items-center gap-1.5 mb-4 overflow-x-auto pb-1 scrollbar-none">
                {CODE_TYPE_TABS.map(tab => (
                    <button key={tab.value} onClick={() => handleTypeChange(tab.value)}
                        className={`flex-shrink-0 px-3 py-1.5 rounded-full text-[11px] font-bold transition-all border ${
                            codeType === tab.value
                                ? 'bg-teal-600 text-white border-teal-600 shadow-sm'
                                : 'bg-white text-gray-500 border-gray-200 hover:border-teal-300 hover:text-teal-600'
                        }`}>
                        {tab.label}
                    </button>
                ))}
                {!isLoading && (searchTerm || category || codeType) && (
                    <span className="ml-auto flex-shrink-0 text-[11px] text-gray-400 pl-2">
                        {data?.totalCount || 0} results
                    </span>
                )}
            </div>

            {/* Results */}
            <SearchResults
                results={data?.results || []}
                loading={isLoading}
                totalCount={data?.totalCount || 0}
                searchTerm={searchTerm || category}
            />

            {/* Guest → login/register modal */}
            <GuestLimitModal isOpen={guestModalOpen} onClose={() => setGuestModalOpen(false)} />

            {/* Logged-in → upgrade plans modal */}
            <PlansModal
                isOpen={plansOpen}
                onClose={() => { setPlansOpen(false); loadCounter(); }}
                reason={planReason}
                message={planMessage}
            />
        </div>
    );
};

export default SearchPage;